/**
 * Asset URL helpers for the game runtime.
 *
 * The PWA build is portable: everything is served relative to the
 * directory the index.html is in, thanks to <base href="./"> in the
 * HTML and PUBLIC_URL='.' at build time. So the right way to point at
 * an asset shipped in client/public/ is a *relative* path like
 * 'game-icons/Patreon.png' — the browser will resolve it against
 * document.baseURI, which is pinned to the PWA directory by <base>.
 *
 * Plain web (admin dashboard) is unaffected: it's served from '/', so
 * relative paths still resolve to the correct files in client/public/.
 *
 * This module replaces the previous PUBLIC_URL-prefixing approach,
 * which produced host-absolute URLs like '/play/foo/v0.1.0-abc/…' and
 * broke the moment the PWA was moved to a different folder.
 */

/**
 * Normalize a path for use in the current runtime.
 *
 *  - http(s)/protocol-relative/data/blob → passed through untouched
 *  - leading '/' → stripped so it resolves against <base href>
 *  - already relative → returned as-is
 *
 * Idempotent: running assetUrl() on its own output yields the same
 * value, so wrapping at multiple layers is safe.
 */
export function assetUrl(p) {
  if (!p || typeof p !== 'string') return p;
  if (
    /^https?:\/\//i.test(p)
    || p.startsWith('//')
    || p.startsWith('data:')
    || p.startsWith('blob:')
  ) {
    return p;
  }
  if (p.startsWith('/')) return p.slice(1);
  return p;
}

/**
 * Walk a loaded game bundle and rewrite every host-absolute or
 * root-absolute '…/game-assets/…' string into its relative counterpart
 * 'game-assets/…'. Used by the standalone data provider so characters /
 * storyflow nodes / insta posts / sound URLs all resolve inside the PWA
 * folder instead of escaping to the R2 bucket root.
 *
 * Handles three shapes we've actually seen in the wild:
 *   '/game-assets/foo.png'                           → 'game-assets/foo.png'
 *   'https://r2.example.com/game-assets/foo.png'     → 'game-assets/foo.png'
 *   'https://cdn.example.com/play/x/game-assets/foo' → passed through untouched
 *
 * The last case matters because the match is anchored at `/game-assets/`
 * as a path *prefix*, not as a substring — we don't want to butcher URLs
 * whose path happens to contain 'game-assets' as a non-prefix segment.
 *
 * Walks arrays and plain objects in place so shared references survive.
 */
export function rewriteGameAssetPaths(value) {
  const rewrite = (s) => {
    if (typeof s !== 'string') return s;
    // Host-absolute URL: strip the scheme+host, then strip the leading '/'.
    const hostAbs = s.match(/^https?:\/\/[^/]+(\/game-assets\/.*)$/i);
    if (hostAbs) return hostAbs[1].slice(1);
    // Root-absolute path: just drop the leading slash.
    if (s.startsWith('/game-assets/')) return s.slice(1);
    return s;
  };

  const walk = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        const v = node[i];
        if (typeof v === 'string') {
          node[i] = rewrite(v);
        } else if (v && typeof v === 'object') {
          walk(v);
        }
      }
      return;
    }
    if (typeof node === 'object') {
      for (const k of Object.keys(node)) {
        const v = node[k];
        if (typeof v === 'string') {
          node[k] = rewrite(v);
        } else if (v && typeof v === 'object') {
          walk(v);
        }
      }
    }
  };

  walk(value);
  return value;
}
